import { useState } from "react";
import type { MfgPricingLayerGridRow } from "@/lib/manufacturing/types";
import { ghostBtnClass, mfgConfirmDialogClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { formatUsd } from "./pricingGridFormat";

function formatQty(quantity: number, unit: string): string {
  const qty = Number.isFinite(quantity) ? String(Number(quantity.toFixed(4))) : "—";
  return unit ? `${qty} ${unit}` : qty;
}

export function ManufacturingPricingMaterialsCell({ row }: { row: MfgPricingLayerGridRow }) {
  const [open, setOpen] = useState(false);
  const missingCount = row.materials_unpriced_leaf_count ?? 0;
  const listed = row.materials_missing_costs ?? [];
  const omitted = Math.max(0, missingCount - listed.length);
  const error = row.materials_error_text?.trim() ?? "";
  const saved = Boolean(row.materials_computed_at);
  const behind = Boolean(saved && row.materials_is_stale && !error);
  const noteLabel = error ? "Couldn't calculate" : missingCount > 0 ? `${missingCount} missing` : "";

  return (
    <div className="inline-flex max-w-full flex-col items-end gap-0.5">
      <span className="tabular-nums">{error ? "—" : formatUsd(row.materials_cost_usd)}</span>
      {noteLabel ? (
        <button
          type="button"
          className="text-[10px] font-medium text-rose-700 underline-offset-2 hover:underline dark:text-rose-300"
          onClick={() => setOpen(true)}
        >
          {noteLabel}
        </button>
      ) : null}
      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cogs-missing-cost-title"
          onClick={() => setOpen(false)}
        >
          <div
            className={`max-h-[min(80vh,32rem)] w-full max-w-lg overflow-y-auto ${mfgConfirmDialogClass}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="cogs-missing-cost-title" className="text-base font-semibold text-gray-900 dark:text-zinc-100">
              {row.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-zinc-400">{row.code}</p>
            {error ? (
              <p className="text-sm text-gray-700 dark:text-zinc-300">{error}</p>
            ) : (
              <p className="text-sm text-gray-700 dark:text-zinc-300">
                {formatUsd(row.materials_cost_usd)} is the saved sum of materials that have a unit cost.
                {missingCount > 0
                  ? ` ${missingCount} material${missingCount === 1 ? "" : "s"} still have no cost and are left out.`
                  : ""}
              </p>
            )}
            {listed.length > 0 ? (
              <ul className="max-h-64 space-y-1 overflow-y-auto text-xs text-gray-700 dark:text-zinc-300">
                {listed.map((item) => (
                  <li key={item.material_id} className="flex items-baseline justify-between gap-3">
                    <span>
                      <span className="font-medium">{item.material_code || "No code"}</span>
                      <span className="text-gray-500 dark:text-zinc-400"> · {item.name}</span>
                    </span>
                    <span className="shrink-0 tabular-nums">{formatQty(item.quantity, item.unit)}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {omitted > 0 ? (
              <p className="text-xs text-gray-500 dark:text-zinc-400">
                {listed.length === 0
                  ? "Names were not stored with this total. Refresh materials on this page to list them."
                  : `${omitted} more ${omitted === 1 ? "is" : "are"} not listed.`}
              </p>
            ) : null}
            {(row.materials_zero_on_hand_leaf_count ?? 0) > 0 ? (
              <p className="text-xs text-gray-500 dark:text-zinc-400">
                {row.materials_zero_on_hand_leaf_count} material
                {row.materials_zero_on_hand_leaf_count === 1 ? "" : "s"} also show nothing on hand. That does not
                change this dollar amount.
              </p>
            ) : null}
            {behind ? (
              <p className="text-xs text-gray-500 dark:text-zinc-400">
                The bill or a material cost changed after this was saved. Refresh materials on this page to update it.
              </p>
            ) : null}
            <div className="flex justify-end">
              <button type="button" className={ghostBtnClass} onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
